/* eslint-disable @next/next/no-img-element */
"use client";

import { useMemo, useState, type KeyboardEvent } from "react";
import type { ProductGalleryImage } from "@/lib/site-data";

type Props = {
  imageLabel: string;
  imageUrl: string;
  images: ProductGalleryImage[];
};

export function ProductGallery({ imageLabel, imageUrl, images }: Props) {
  const galleryImages = useMemo(() => {
    const items = images.length ? images : [{ url: imageUrl, alt: imageLabel, label: imageLabel }];
    return items.map((item, index) => ({
      url: item.url || (index === 0 ? imageUrl : ""),
      alt: item.alt || imageLabel,
      label: item.label || item.alt || imageLabel,
    }));
  }, [imageLabel, imageUrl, images]);
  const [activeIndex, setActiveIndex] = useState(0);
  const active = galleryImages[activeIndex] ?? galleryImages[0];

  function goTo(index: number) {
    setActiveIndex((index + galleryImages.length) % galleryImages.length);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") { event.preventDefault(); goTo(activeIndex - 1); }
    if (event.key === "ArrowRight") { event.preventDefault(); goTo(activeIndex + 1); }
    if (event.key === "Home") { event.preventDefault(); goTo(0); }
    if (event.key === "End") { event.preventDefault(); goTo(galleryImages.length - 1); }
  }

  return (
    <section aria-label="Product image gallery" className="grid gap-4" onKeyDown={onKeyDown} tabIndex={0}>
      <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-white outline-none">
        <GalleryFrame image={active} />
        {galleryImages.length > 1 ? <><ArrowButton label="Previous product image" direction="left" onClick={() => goTo(activeIndex - 1)} /><ArrowButton label="Next product image" direction="right" onClick={() => goTo(activeIndex + 1)} /></> : null}
      </div>
      {galleryImages.length > 1 ? <div className="grid grid-cols-3 gap-3 sm:grid-cols-5" aria-label="Product image thumbnails">{galleryImages.map((image, index) => <button key={image.label + index} type="button" aria-label={"Show " + image.alt} aria-current={index === activeIndex ? "true" : undefined} onClick={() => goTo(index)} className={"overflow-hidden rounded-md border bg-white text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#F68A00] " + (index === activeIndex ? "border-[#F68A00]" : "border-slate-200 hover:border-[#14B8A6]")}><div className="aspect-[4/3]"><GalleryFrame image={image} thumbnail /></div><span className="block truncate px-2 py-2 text-xs font-semibold text-[#334155]">{image.label}</span></button>)}</div> : null}
    </section>
  );
}

function GalleryFrame({ image, thumbnail = false }: { image: ProductGalleryImage; thumbnail?: boolean }) {
  if (image.url) {
    return <div className={(thumbnail ? "h-full w-full p-2" : "transition-opacity duration-200 aspect-[4/3] ") + "overflow-hidden bg-[#F8FAFC]"}><img src={image.url} alt={image.alt} loading="lazy" decoding="async" className={(thumbnail ? "object-contain p-2" : "object-contain") + " h-full w-full"} /></div>;
  }
  return <div role="img" aria-label={image.alt} className={(thumbnail ? "" : "transition-opacity duration-200 ") + "flex aspect-[4/3] items-center justify-center bg-[linear-gradient(135deg,#111827,#1F2937,#14B8A6)] p-5 text-center text-xl font-bold tracking-[0.08em] text-white sm:text-4xl"}>{image.label}</div>;
}

function ArrowButton({ label, direction, onClick }: { label: string; direction: "left" | "right"; onClick: () => void }) {
  return <button type="button" aria-label={label} onClick={onClick} className={(direction === "left" ? "left-3" : "right-3") + " absolute top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-slate-200 bg-white/95 text-xl font-semibold text-[#111827] shadow-sm transition-colors hover:border-[#F68A00] focus:outline-none focus:ring-2 focus:ring-[#F68A00]"}><span aria-hidden="true">{direction === "left" ? "<" : ">"}</span></button>;
}
